<script setup>
import { computed, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import CustomAttributeBundlesAPI from 'dashboard/api/customAttributeBundles';
import { convertToAttributeSlug } from 'dashboard/helper/commons.js';

const props = defineProps({
  attributes: { type: Array, default: () => [] },
  attributeModel: { type: String, required: true },
  onClose: { type: Function, default: () => {} },
});

const { t } = useI18n();
const bundleName = ref('');
const bundleKey = ref('');
const description = ref('');
const selectedFields = ref([]);
const show = ref(true);
const isSaving = ref(false);

const availableAttributes = computed(() =>
  props.attributes.filter(
    attr =>
      !selectedFields.value.some(
        field => field.attribute_key === attr.attribute_key
      )
  )
);

const canSave = computed(
  () =>
    bundleName.value.trim() &&
    bundleKey.value.trim() &&
    selectedFields.value.length > 0 &&
    !isSaving.value
);

const onNameChange = value => {
  bundleName.value = value;
  bundleKey.value = convertToAttributeSlug(value);
};

const addField = attributeKey => {
  if (!attributeKey) return;

  const attr = props.attributes.find(item => item.attribute_key === attributeKey);
  if (!attr) return;

  selectedFields.value = [
    ...selectedFields.value,
    {
      attribute_key: attr.attribute_key,
      label: attr.attribute_display_name,
      required: true,
      order: selectedFields.value.length + 1,
    },
  ];
};

const removeField = index => {
  selectedFields.value = selectedFields.value
    .filter((_, i) => i !== index)
    .map((field, i) => ({ ...field, order: i + 1 }));
};

const moveField = (index, direction) => {
  const target = index + direction;
  if (target < 0 || target >= selectedFields.value.length) return;

  const fields = [...selectedFields.value];
  const [field] = fields.splice(index, 1);
  fields.splice(target, 0, field);
  selectedFields.value = fields.map((item, i) => ({ ...item, order: i + 1 }));
};

const toggleRequired = index => {
  selectedFields.value = selectedFields.value.map((field, i) =>
    i === index ? { ...field, required: !field.required } : field
  );
};

const createBundle = async () => {
  if (!canSave.value) return;

  isSaving.value = true;
  try {
    await CustomAttributeBundlesAPI.create({
      attribute_model: props.attributeModel,
      bundle_name: bundleName.value.trim(),
      bundle_key: bundleKey.value.trim(),
      description: description.value.trim(),
      fields: selectedFields.value.map(({ attribute_key, required, order }) => ({
        attribute_key,
        required,
        order,
      })),
    });
    useAlert(t('ATTRIBUTES_MGMT.BUNDLE.SUCCESS_MESSAGE'));
    props.onClose();
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      t('ATTRIBUTES_MGMT.BUNDLE.ERROR_MESSAGE');
    useAlert(message);
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <woot-modal v-model:show="show" :on-close="onClose">
    <div class="flex max-h-[80vh] flex-col overflow-auto">
      <woot-modal-header
        :header-title="$t('ATTRIBUTES_MGMT.BUNDLE.TITLE')"
      />

      <form class="flex w-full flex-col gap-4" @submit.prevent="createBundle">
        <woot-input
          :model-value="bundleName"
          :label="$t('ATTRIBUTES_MGMT.BUNDLE.NAME')"
          type="text"
          :placeholder="$t('ATTRIBUTES_MGMT.BUNDLE.NAME_PLACEHOLDER')"
          @update:model-value="onNameChange"
        />
        <woot-input
          v-model="bundleKey"
          :label="$t('ATTRIBUTES_MGMT.BUNDLE.KEY')"
          type="text"
          :placeholder="$t('ATTRIBUTES_MGMT.BUNDLE.KEY_PLACEHOLDER')"
        />
        <label>
          {{ $t('ATTRIBUTES_MGMT.BUNDLE.DESCRIPTION') }}
          <textarea
            v-model="description"
            rows="3"
            :placeholder="$t('ATTRIBUTES_MGMT.BUNDLE.DESCRIPTION_PLACEHOLDER')"
          />
        </label>

        <label>
          {{ $t('ATTRIBUTES_MGMT.BUNDLE.ADD_ATTRIBUTE') }}
          <select
            :disabled="availableAttributes.length === 0"
            @change="
              addField($event.target.value);
              $event.target.value = '';
            "
          >
            <option value="">
              {{ $t('ATTRIBUTES_MGMT.BUNDLE.SELECT_ATTRIBUTE') }}
            </option>
            <option
              v-for="attribute in availableAttributes"
              :key="attribute.attribute_key"
              :value="attribute.attribute_key"
            >
              {{ attribute.attribute_display_name }} ({{
                attribute.attribute_key
              }})
            </option>
          </select>
        </label>

        <div class="flex flex-col gap-2">
          <div
            v-for="(field, index) in selectedFields"
            :key="field.attribute_key"
            class="flex items-center gap-2 rounded-lg border border-n-weak bg-n-solid-2 p-2"
          >
            <span class="i-lucide-grip-vertical text-n-slate-10" />
            <div class="min-w-0 flex-1">
              <div class="truncate text-sm font-medium text-n-slate-12">
                {{ field.label }}
              </div>
              <div class="truncate font-mono text-xs text-n-slate-10">
                {{ field.attribute_key }}
              </div>
            </div>
            <label class="flex items-center gap-1 text-xs text-n-slate-11">
              <input
                type="checkbox"
                :checked="field.required"
                @change="toggleRequired(index)"
              />
              {{ $t('ATTRIBUTES_MGMT.BUNDLE.REQUIRED') }}
            </label>
            <Button
              icon="i-lucide-arrow-up"
              slate
              xs
              faded
              type="button"
              :disabled="index === 0"
              @click="moveField(index, -1)"
            />
            <Button
              icon="i-lucide-arrow-down"
              slate
              xs
              faded
              type="button"
              :disabled="index === selectedFields.length - 1"
              @click="moveField(index, 1)"
            />
            <Button
              icon="i-lucide-trash-2"
              ruby
              xs
              faded
              type="button"
              @click="removeField(index)"
            />
          </div>
          <p v-if="selectedFields.length === 0" class="text-sm text-n-slate-10">
            {{ $t('ATTRIBUTES_MGMT.BUNDLE.EMPTY_FIELDS') }}
          </p>
        </div>

        <div class="modal-footer">
          <Button
            slate
            faded
            type="button"
            :label="$t('ATTRIBUTES_MGMT.BUNDLE.CANCEL')"
            @click="onClose"
          />
          <Button
            type="submit"
            :label="$t('ATTRIBUTES_MGMT.BUNDLE.SUBMIT')"
            :is-loading="isSaving"
            :disabled="!canSave"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
